/* eslint-disable react-dom/no-dangerously-set-innerhtml */
import { Box, Grid, Typography } from '@mui/material';
import { ScrollMode } from '@react-pdf-viewer/core';
import DOMPurify from 'dompurify';
import { useCallback, useMemo } from 'react';
import ImagePanZoomRotate from 'react-image-pan-zoom-rotate';

import { PdfViewer } from '@/app/shared/components/PdfViewer';
import { HTMLFileWrapper, StipReviewGrid, StipReviewGridVertical } from './stips.styles';
import type { DocumentItem, IStip } from './stips.types';

const imageExts = ['jpeg', 'jpg', 'png', 'gif'];
const previewNotAvailable = 'Preview not available';

interface IStipsReviewProps {
    contactStipData: DocumentItem;
    stipData: IStip;
}

export const DocumentPreview = ({ contactStipData, stipData }: IStipsReviewProps) => {

    const extension = useMemo(() => {
        // This regex looks for a dot, then captures only letters/numbers
        // until it hits a space, a parenthesis, or the end of the line.
        // e.g. "document.pdf", "image.jpeg (Deleted)", "file.name.with.dots.txt"
        const match = contactStipData.fileName.match(/\.([a-z0-9]+)(?=[^a-z0-9]|$)/i);

        return match ? match[1] : contactStipData.fileName;
    }, [contactStipData.fileName]);

    // Logic: Use blobDownloadLink if it exists, otherwise build the Base64 string
    const resolvedImage = useMemo(() => {
        if (stipData?.blobDownloadLink) {
            return stipData.blobDownloadLink;
        }

        if (stipData?.content) {
            // You can use a default extension like 'png' if stipReview.extension is missing
            const ext = extension || 'png';
            return `data:image/${ext};base64,${stipData.content}`;
        }

        return ''; // Fallback for when data is still loading
    }, [stipData, extension]);

    const getHtml = useCallback((content = '') => {
        // Decode base64 content
        const html = atob(content);

        // sanitize html
        const sanitizedHtml = DOMPurify.sanitize(html?.trim());

        // Return an object with the __html property
        return { __html: sanitizedHtml };
    }, []);

    return (
        <Box>
            {extension.toLocaleLowerCase() === 'pdf' ? (
                <Grid
                    item
                    marginLeft={1.5}
                    xs={5.8}
                >
                    <PdfViewer
                        scrollMode={ScrollMode.Horizontal}
                        fileSource={stipData?.blobDownloadLink || stipData?.content}
                    />
                </Grid>
            ) : imageExts.includes(extension.toLowerCase()) ? (
                <StipReviewGrid
                    item
                    xs={6}
                >
                    <Box
                        sx={{
                            height: '80vh',
                            overflow: 'hidden',
                            '& div': {
                                top: '7rem !important',
                                right: '2rem !important'
                            },
                            '& img': {
                            maxWidth: '100%',
                            maxHeight: '80vh',
                            objectFit: 'contain',
                            },
                        }}
                    >
                        <ImagePanZoomRotate
                            image={resolvedImage}
                        />
                    </Box>
                </StipReviewGrid>
            ) : extension.toLocaleLowerCase() === 'html' ? (
                <Grid
                    item
                    xs={6}
                >
                    <HTMLFileWrapper>
                        <Box dangerouslySetInnerHTML={getHtml(stipData?.content || '')} />
                    </HTMLFileWrapper>
                </Grid>
            ) : (
                <StipReviewGridVertical
                    item
                    xs={6}
                >
                    <Typography variant="h2">{previewNotAvailable}</Typography>
                </StipReviewGridVertical>
            )}
        </Box>
    );
};
